import ExcelJS from 'exceljs';
import { CargoEventReportData } from '../../ReportGeneratorTypes';
import { setCellValueWithFont, setupWorksheetForPrint, setWorksheetPrintArea } from './reportEventExcelUtils';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import {
    addOdfjellHeader,
    addVesselDetails,
    getCargoOperationForEvent,
    getStringValueRegFromEvent,
    getTankListStrFromEvent,
    getWeightStr,
    odfjellHeaderColor,
} from './parts/helpers';
import { TankersEvent } from 'src/types/ViewModel/TankersEvent';

const addReportHeader = (worksheet: ExcelJS.Worksheet, logoImageId: number): void => {
    let rowNum = addOdfjellHeader(worksheet, logoImageId);
    rowNum = 6;
    setCellValueWithFont(worksheet, rowNum, 1, `EMPTY TANK CERTIFICATE`, {
        bold: true,
        color: odfjellHeaderColor,
        fontSize: 16,
        mergeAcross: 9,
    });
};

const addCargoInfo = (worksheet: ExcelJS.Worksheet, selectedEvent: TankersEvent, cargoOperation: RotationCargoOperation, rowNumber: number) => {
    // Intro line
    rowNumber += 3;
    setCellValueWithFont(worksheet, rowNumber, 1, 'This is to certify that upon completion of discharge:', { mergeAcross: 9, align: 'left' });
    rowNumber += 2;

    // Header labels (same row)
    const headerStyle = { align: 'left', bold: true } as const;
    (
        [
            { col: 1, text: 'Cargo No.:', style: { ...headerStyle, mergeAcross: 2 } },
            { col: 3, text: 'Quantity:', style: { ...headerStyle, mergeAcross: 2 } },
            { col: 5, text: 'Cargo Name:', style: { ...headerStyle, mergeAccross: 2 } },
            { col: 7, text: 'Stowage:', style: { ...headerStyle, mergeAcross: 3 } },
        ] as const
    ).forEach((h) => setCellValueWithFont(worksheet, rowNumber, h.col, h.text, h.style));

    // Values (next row)
    rowNumber++;
    const tankList = getTankListStrFromEvent(selectedEvent, 66);
    setCellValueWithFont(worksheet, rowNumber, 1, selectedEvent.cargoNumber, { mergeAcross: 2, align: 'left' });
    setCellValueWithFont(worksheet, rowNumber, 3, getWeightStr(cargoOperation, true), { mergeAcross: 2, align: 'left' });
    setCellValueWithFont(worksheet, rowNumber, 5, cargoOperation.cargo.product.tradeName, {
        align: 'left',
        verticalAlign: 'top',
        mergeAcross: 2,
        mergeDown: 3,
        wrapText: true,
    });
    setCellValueWithFont(worksheet, rowNumber, 7, tankList, {
        mergeAcross: 3,
        align: 'left',
        verticalAlign: 'top',
        wrapText: true,
        mergeDown: 10,
    });

    return (rowNumber += 11);
};

const addRemarks = (worksheet: ExcelJS.Worksheet, event: TankersEvent, rowNumber: number): number => {
    rowNumber++;
    setCellValueWithFont(
        worksheet,
        rowNumber,
        1,
        `I have inspected subject tank(s), line(s) and pump(s), and found same to be well emptied and drained. As far as visual inspection was possible, no loss of cargo occured onboard during discharge.`,
        {
            mergeAcross: 9,
            mergeDown: 2,
            wrapText: true,
            align: 'left',
            verticalAlign: 'top',
        }
    );

    rowNumber += 3;
    setCellValueWithFont(worksheet, rowNumber, 1, `Remarks:`, {
        mergeAcross: 9,
        align: 'left',
    });
    rowNumber += 2;
    const remarks = getStringValueRegFromEvent(event, 108);
    setCellValueWithFont(worksheet, rowNumber, 1, remarks, {
        mergeAcross: 9,
        mergeDown: 6,
        wrapText: true,
        verticalAlign: 'top',
        align: 'left',
    });

    return (rowNumber += 7);
};
const addSignature = (worksheet: ExcelJS.Worksheet, rowNumber: number): number => {
    rowNumber += 3;

    setCellValueWithFont(
        worksheet,
        rowNumber,
        6,
        `Surveyor
(Name, Company and Signature)`,
        {
            mergeAcross: 4,
            mergeDown: 2,
            align: 'center',
            verticalAlign: 'top',
            wrapText: true,
        }
    );

    return rowNumber + 6;
};

const setDefaultColumnWidths = (workheet: ExcelJS.Worksheet) => {
    workheet.getColumn(1).width = 9;
    workheet.getColumn(2).width = 3;
    workheet.getColumn(3).width = 14;
    workheet.getColumn(4).width = 21;
    workheet.getColumn(5).width = 9;
    workheet.getColumn(6).width = 17;
    workheet.getColumn(7).width = 9;
    workheet.getColumn(8).width = 14;
    workheet.getColumn(9).width = 7;
};

export const generateEmptyTankCertificateReport = (
    workbook: ExcelJS.Workbook,
    cargoEventReportData: CargoEventReportData,
    logoImageId: number
): void => {
    const _worksheet = workbook.addWorksheet('Empty tank certificate');
    setupWorksheetForPrint(_worksheet);
    // Hide gridlines and keep a clean white background
    _worksheet.views = [{ state: 'normal', showGridLines: false }];
    const portCall = cargoEventReportData.selectedPortCall;
    const selectedEvent = cargoEventReportData.tankersEvents.find((ev) => !ev.deleted && ev.eventId === cargoEventReportData.excelReportInfo.eventId);
    const cargoOperation = getCargoOperationForEvent(
        portCall.berthCalls.flatMap((berthCall) => berthCall.cargoOperations),
        selectedEvent
    );
    if (!cargoOperation || !selectedEvent) return;
    setDefaultColumnWidths(_worksheet);
    addReportHeader(_worksheet, logoImageId);
    let rowNumber = addVesselDetails(
        _worksheet,
        8,
        1,
        7,
        portCall.voyageNumber,
        portCall.portDisplayName,
        cargoEventReportData.vessel,
        null,
        selectedEvent.eventDate.actualDateTimeLocal
    );
    rowNumber = addCargoInfo(_worksheet, selectedEvent, cargoOperation, rowNumber);
    rowNumber = addRemarks(_worksheet, selectedEvent, rowNumber);
    rowNumber = addSignature(_worksheet, rowNumber);

    setWorksheetPrintArea(_worksheet, rowNumber, 9);
};
